// Phase 59 / Phase 80 — Frontend mirror of /app/backend/lib/dedup.py
// MUST stay in sync with the Python implementation so fingerprints
// computed on the client (CSV import, manual add, opening balance,
// recurring runs) match those computed by the PDF worker and by the
// SuperAdmin Duplicate Cleanup tool.
//
// SHA-256 is reached via the subtle-crypto API which is available in
// every browser our app supports. We surface an async fingerprint
// builder so callers don't need to import a hashing lib.
//
// Phase 80: Added reference-id extraction so payee-name truncation
// (e.g., "KAKUMANI RAMADEVI" vs "KAKUMANI R") on the same UPI RRN /
// bank internal txn UUID collapses to the SAME fingerprint. This is
// the fix for the recurring duplicate-transactions bug reported by
// beta users. Card-number masks (BIN + Xs + last4) are explicitly
// ignored so UAE Visa/MC statements don't false-positive-collapse.

const RAIL_TOKENS = new Set([
  'upi', 'neft', 'imps', 'rtgs', 'nft', 'ach', 'chq', 'pos', 'atm',
  'wdl', 'tfr', 'dep', 'card', 'ref', 'txn', 'trf', 'trn', 'trans',
  'payment', 'paid', 'received', 'credit', 'debit', 'transfer',
  'transferred', 'fund', 'funds', 'via', 'from', 'to', 'for', 'the',
  'and', 'cash', 'online', 'merchant', 'purchase', 'purch', 'withdrawal',
  'deposit', 'interest', 'charges', 'charge', 'fee', 'fees', 'tax',
  'service', 'tran',
]);

const RX_UPI_HANDLE = /[\w.]*@[\w.]+/g;
const RX_IFSC_LIKE = /\b[a-z]{4}\d[a-z0-9]+\b/g;
const RX_DATE_STAMP = /\b\d{1,2}[/\-]\d{1,2}[/\-]\d{2,4}\b/g;
const RX_MONEY_DEC = /\b\d{1,3}(?:,\d{3})+(?:\.\d{1,2})?\b/g;
const RX_MONEY_DOT = /\b\d+\.\d{2}\b/g;
const RX_LONG_DIGITS = /\b\d{6,}\b/g;
const RX_SEPARATORS = /[\-_/.;:|]+/g;
const RX_WHITESPACE = /\s+/g;

// =============================================================================
// Phase 80 — Reference-ID extraction (mirror of extract_reference in dedup.py)
// =============================================================================
// Card mask: BIN + Xs + last-4. NEVER a per-txn reference — treat as noise.
const RX_CARD_MASK = /\b\d{4,6}X{4,10}\d{2,4}\b/gi;
const RX_ANY_XMASK = /X{4,}/i;

// Ordered specificity-first. Each entry: [kind, regex]. The regex should
// use a capture group when the "canonical" ref is embedded in a keyword.
const REF_PATTERNS = [
  // 1. Bank-internal txn UUID (STRONGEST signal)
  [
    'internal_uuid',
    /\b(?:ICI|AIR|IBL|SBI|HDF|CRD|crd|AXI|HDFC|IND|GKQ|NPCI|FDRL|UTIB|YESB|CNRB|BARB|KKBK|PUNB|BOFA)[A-Za-z0-9]{20,40}\b/,
  ],
  // 2. NEFT / RTGS UTR
  ['neft_utr', /\b[A-Z]{4}[HRN]\d{9,15}\b/],
  // 3. Explicit "Ref XXXXX"
  ['ref_kw', /\bRef[\s:]+([A-Z0-9]{10,24})\b/i],
  // 4. UPI 12-digit RRN
  ['upi_rrn', /(?<![\dXx])\d{12}(?![\dXx])/],
  // 5. NACH mandate
  ['nach_mandate', /NACH\b[^\d]{0,40}(\b\d{4}[A-Z0-9]{12,24}\b)/i],
  // 6. IMPS ref
  ['imps_ref', /IMPS[-\s]+(\d{10,16})/i],
  // 7. Axis "Chq:" reference
  ['chq_ref', /Chq:\s*(\d{9,16})/i],
];

/**
 * Extract a stable per-transaction bank reference id from a description.
 * Returns { kind, ref } if found (ref uppercased) or { kind: null, ref: null }.
 *
 * Card-number masks are stripped upfront so a card slip never accidentally
 * becomes a per-txn reference.
 */
export function extractReference(desc) {
  if (!desc) return { kind: null, ref: null };
  const cleaned = String(desc).replace(RX_CARD_MASK, ' ');
  for (const [kind, pat] of REF_PATTERNS) {
    const m = cleaned.match(pat);
    if (!m) continue;
    const candidate = m[1] || m[0];
    if (RX_ANY_XMASK.test(candidate)) continue;
    return { kind, ref: candidate.toUpperCase() };
  }
  return { kind: null, ref: null };
}

// Merge solo single-letter alpha tokens with the following token. Drops
// trailing single-letter tokens. Mirrors `_merge_single_chars` in
// dedup.py — see the docstring there for rationale.
function mergeSingleChars(tokens) {
  const out = [];
  for (let i = 0; i < tokens.length; ) {
    const t = tokens[i];
    if (t.length === 1 && /^[a-z]$/i.test(t) && i + 1 < tokens.length) {
      out.push(t + tokens[i + 1]);
      i += 2;
    } else if (t.length === 1) {
      i += 1;
    } else {
      out.push(t);
      i += 1;
    }
  }
  return out;
}

function dedupeAll(tokens) {
  const seen = new Set();
  const out = [];
  for (const t of tokens) {
    if (!seen.has(t)) { seen.add(t); out.push(t); }
  }
  return out;
}

/**
 * Normalize a transaction description down to its merchant-identity
 * core. Idempotent. Lower-case ASCII, no punctuation, sorted-first-seen
 * unique tokens of length >= 3, with bank-rail words filtered out.
 */
export function normalizeDescription(desc) {
  if (!desc) return '';
  let s = String(desc).toLowerCase().trim();
  s = s.replace(RX_UPI_HANDLE, ' ');
  s = s.replace(RX_IFSC_LIKE, ' ');
  s = s.replace(RX_DATE_STAMP, ' ');
  s = s.replace(RX_MONEY_DEC, ' ');
  s = s.replace(RX_MONEY_DOT, ' ');
  s = s.replace(RX_LONG_DIGITS, ' ');
  s = s.replace(RX_SEPARATORS, ' ');
  s = s.replace(RX_WHITESPACE, ' ').trim();
  if (!s) return '';
  let tokens = s.split(' ');
  tokens = mergeSingleChars(tokens);
  tokens = tokens.filter((t) => t.length >= 3 && /^[a-z]+$/i.test(t) && !RAIL_TOKENS.has(t));
  tokens = dedupeAll(tokens);
  return tokens.join(' ');
}

async function sha256Hex(input) {
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Async because subtle-crypto is async. Truncated to 32 hex chars to
 * match the Python side exactly.
 *
 * Phase 80: prefers a stable bank REFERENCE id when one can be
 * extracted; falls back to normalized-description hashing when not.
 * This mirrors backend/lib/dedup.py::transaction_fingerprint().
 */
export async function transactionFingerprint({
  userId,
  accountId,
  txnDate,
  amount,
  description,
}) {
  const { ref } = extractReference(description || '');
  const amountPart = Math.abs(Number(amount || 0)).toFixed(2);
  let raw;
  if (ref) {
    raw = [userId, accountId || '_', txnDate || '_', amountPart, `REF:${ref}`].join('|');
  } else {
    const norm = normalizeDescription(description || '');
    raw = [userId, accountId || '_', txnDate || '_', amountPart, `TXT:${norm}`].join('|');
  }
  const hex = await sha256Hex(raw);
  return hex.slice(0, 32);
}
